import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import GlobalWorkout from './GlobalWorkout';

/**
 * StarredWorkout Model
 *
 * Manages user's starred workout relationships
 * Firestore path: users/{userId}/starredWorkouts/{workoutId}
 */
class StarredWorkout {
  /**
   * Star a global workout (adds to starred collection + increments popularity)
   * @param {string} userId - The user ID
   * @param {string} workoutId - The global workout ID
   * @returns {Promise<void>}
   */
  static async star(userId, workoutId) {
    try {
      // Add to user's starred collection
      const starredRef = doc(db, 'users', userId, 'starredWorkouts', workoutId);
      await setDoc(starredRef, {
        workoutId,
        starredAt: serverTimestamp()
      });

      // Increment popularity count
      await GlobalWorkout.incrementPopularity(workoutId);
    } catch (error) {
      console.error('Error starring workout:', error);
      throw error;
    }
  }

  /**
   * Unstar a workout (removes from starred + decrements popularity)
   * @param {string} userId - The user ID
   * @param {string} workoutId - The workout ID
   * @returns {Promise<void>}
   */
  static async unstar(userId, workoutId) {
    try {
      // Remove from user's starred collection
      const starredRef = doc(db, 'users', userId, 'starredWorkouts', workoutId);
      await deleteDoc(starredRef);

      // Decrement popularity count
      await GlobalWorkout.decrementPopularity(workoutId);
    } catch (error) {
      console.error('Error unstarring workout:', error);
      throw error;
    }
  }

  /**
   * Check if a workout is starred by user
   * @param {string} userId - The user ID
   * @param {string} workoutId - The workout ID
   * @returns {Promise<boolean>} True if starred, false otherwise
   */
  static async isStarred(userId, workoutId) {
    try {
      const starredRef = doc(db, 'users', userId, 'starredWorkouts', workoutId);
      const docSnap = await getDoc(starredRef);
      return docSnap.exists();
    } catch (error) {
      console.error('Error checking starred status:', error);
      throw error;
    }
  }

  /**
   * Get array of starred workout IDs
   * @param {string} userId - The user ID
   * @returns {Promise<Set<string>>} Set of starred workout IDs
   */
  static async getStarredIds(userId) {
    try {
      const starredRef = collection(db, 'users', userId, 'starredWorkouts');
      const snapshot = await getDocs(starredRef);
      return new Set(snapshot.docs.map(doc => doc.id));
    } catch (error) {
      console.error('Error fetching starred workout IDs:', error);
      throw error;
    }
  }

  /**
   * Get full starred workout data (joins with globalWorkouts)
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} Array of starred workout objects with full data
   */
  static async getStarredWorkouts(userId) {
    try {
      // Get starred workout IDs
      const starredIds = await this.getStarredIds(userId);

      if (starredIds.size === 0) {
        return [];
      }

      // Fetch full workout data for each starred ID
      const workoutPromises = Array.from(starredIds).map(id =>
        GlobalWorkout.getById(id)
      );

      const workouts = await Promise.all(workoutPromises);

      // Filter out any null results (deleted workouts)
      return workouts.filter(workout => workout !== null);
    } catch (error) {
      console.error('Error fetching starred workouts:', error);
      throw error;
    }
  }
}

export default StarredWorkout;
